import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark, MoreVertical, Volume2, Square } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { categoryIcon, categoryAccentStyle } from '../lib/categories';
import { firstNameOf, formatRecDate } from '../lib/format';
import { Avatar } from './Avatar';
import { useSpeech, speakRecommendation, stopSpeech, spokenText } from '../lib/speech';
import { shareOrCopy, shareTextFor } from '../lib/share';
import { VisibilityBadge } from '../lib/visibility';
import * as api from '../lib/api';
import { toast } from 'sonner';

const REPORT_REASONS = [
  { value: 'inappropriate', label: 'Inappropriate or offensive' },
  { value: 'spam_misleading', label: 'Spam or misleading' },
  { value: 'harassment_harmful', label: 'Harassment or harmful' },
  { value: 'something_else', label: 'Something else' },
];

// Identity-first card: person -> recommendation -> why.
export const RecommendationCard = ({ recommendation, onSaveChange, onDeleted, onReported, allowOwnerUnsave }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [saved, setSaved] = useState(recommendation.isSaved);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [savePop, setSavePop] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const { supported: canSpeak, speaking } = useSpeech(recommendation.id);

  const closeMenu = () => {
    setShowMenu(false);
    triggerRef.current?.focus();
  };

  // Escape closes the menu wherever focus currently sits.
  useEffect(() => {
    if (!showMenu) return;
    menuRef.current?.querySelector('button')?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeMenu();
        return;
      }
      // Keep Tab inside the open menu so a keyboard user cannot reach the cards
      // sitting behind the backdrop.
      if (e.key !== 'Tab') return;
      const items = Array.from(menuRef.current?.querySelectorAll('button') || []);
      if (!items.length) return;
      e.preventDefault();
      const at = items.indexOf(document.activeElement);
      const next = e.shiftKey
        ? (at <= 0 ? items.length - 1 : at - 1)
        : (at === items.length - 1 ? 0 : at + 1);
      items[next].focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showMenu]);

  // Escape also dismisses the delete confirmation, matching Account settings.
  useEffect(() => {
    if (!showDeleteConfirm) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !deleting) setShowDeleteConfirm(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showDeleteConfirm, deleting]);

  const isOwn = user?.id === recommendation.authorId;
  const authorName = recommendation.authorName || 'Unknown';
  const firstName = firstNameOf(authorName);
  const CategoryIcon = categoryIcon(recommendation.category);

  const handleSaveToggle = async () => {
    if (!isAuthenticated) {
      navigate('/signin', { state: { from: window.location.pathname, action: 'save' } });
      return;
    }
    setSavePop(true);
    setTimeout(() => setSavePop(false), 200);
    try {
      if (saved) {
        await api.unsaveRecommendation(recommendation.id);
        setSaved(false);
        onSaveChange?.(recommendation.id, false);
        toast.success('Removed from Saved');
      } else {
        await api.saveRecommendation(recommendation.id);
        setSaved(true);
        onSaveChange?.(recommendation.id, true);
        toast.success('Saved');
      }
    } catch (err) {
      toast.error('Could not update Saved');
    }
  };

  // Called straight from the tap so iOS accepts it as a user gesture.
  const handleReadAloud = () => {
    if (speaking) {
      stopSpeech();
      return;
    }
    speakRecommendation(recommendation.id, spokenText(recommendation, isOwn), () =>
      toast.error('Check your ringer or volume'),
    );
  };

  // Straight from the tap, so iOS accepts the share sheet.
  const handleShare = () => {
    setShowMenu(false);
    shareOrCopy({
      title: 'irecommend',
      text: shareTextFor(recommendation, isOwn),
      url: window.location.origin,
    }).then((result) => {
      if (result === 'copied') toast.success('Copied to clipboard');
      if (result === 'failed') toast.error('Could not share this recommendation');
    });
  };

  const handleReport = async () => {
    if (!reportReason) return;
    setReporting(true);
    try {
      await api.reportRecommendation(recommendation.id, reportReason);
      setShowReport(false);
      setRemoved(true);
      onReported?.(recommendation.id);
      toast.success('Thanks, we have received your report', {
        description: "You won't see this recommendation in Explore again.",
      });
    } catch (err) {
      const already = err?.response?.status === 400;
      toast.error(already ? 'You have already reported this' : 'Could not send your report');
      setReporting(false);
      if (already) {
        setShowReport(false);
        setRemoved(true);
        onReported?.(recommendation.id);
      }
    }
  };

  const handleDelete = async () => {    setDeleting(true);
    try {
      await api.deleteRecommendation(recommendation.id);
      toast.success('Recommendation deleted');
      setShowDeleteConfirm(false);
      setRemoved(true);
      onDeleted?.(recommendation.id);
    } catch (err) {
      toast.error('Failed to delete recommendation');
      setDeleting(false);
    }
  };

  if (removed) return null;

  return (
    <article
      className="rc-card"
      style={categoryAccentStyle(recommendation.category)}
      data-testid={`rec-${recommendation.id}`}
    >
      <div className="rc-top">
        <Avatar
          userId={recommendation.authorId}
          name={authorName}
          photoUrl={recommendation.authorPhotoUrl}
          className="rc-avatar"
          testId={`rec-avatar-${recommendation.id}`}
        />
        <div className="rc-identity">
          <span className="rc-name" data-testid="rec-author">{authorName}</span>
          <span className="rc-date" data-testid="rec-date">{formatRecDate(recommendation.createdAt)}</span>        </div>
        <div className="rc-actions">
        {canSpeak && (
          <button
            className={`rc-speak ${speaking ? 'speaking' : ''}`}
            onClick={handleReadAloud}
            aria-pressed={speaking}
            aria-label={speaking
              ? `Stop reading ${recommendation.name}`
              : `Read ${recommendation.name} aloud`}
            data-testid={`speak-btn-${recommendation.id}`}
          >
            {speaking ? <Square className="rc-speak-icon" /> : <Volume2 className="rc-speak-icon" />}
          </button>
        )}
        {!isOwn && (
          <button
            className={`rc-save ${saved ? 'active' : ''} ${savePop ? 'pop' : ''}`}
            onClick={handleSaveToggle}
            aria-pressed={saved}
            aria-label={saved ? `Remove ${recommendation.name} from Saved` : `Save ${recommendation.name}`}
            data-testid={`save-btn-${recommendation.id}`}
          >
            <Bookmark className="rc-save-icon" fill={saved ? 'currentColor' : 'none'} />
            <span>{saved ? 'Saved' : 'Save'}</span>
          </button>
        )}
        <div className="owner-menu-wrap">
          <button
            className="owner-menu-btn"
            ref={triggerRef}
            onClick={() => (showMenu ? closeMenu() : setShowMenu(true))}
            aria-label="Recommendation options"
            aria-haspopup="menu"
            aria-expanded={showMenu}
            data-testid={`owner-menu-${recommendation.id}`}
          >
            <MoreVertical />
          </button>
          {showMenu && (
            <>
              <div className="owner-menu-backdrop" onClick={closeMenu} />
              <div
                className="owner-menu"
                role="menu"
                ref={menuRef}
                data-testid={`owner-menu-open-${recommendation.id}`}
              >
                {isOwn && (
                  <button
                    className="owner-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setShowMenu(false);
                      navigate(`/edit/${recommendation.id}`);
                    }}
                    data-testid={`edit-rec-${recommendation.id}`}
                  >
                    Edit recommendation
                  </button>
                )}
                <button
                  className="owner-menu-item"
                  role="menuitem"
                  onClick={handleShare}
                  data-testid={`share-rec-${recommendation.id}`}
                >
                  Share recommendation
                </button>
                {isOwn && allowOwnerUnsave && saved && (
                  <button
                    className="owner-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setShowMenu(false);
                      handleSaveToggle();
                    }}
                    data-testid={`owner-unsave-${recommendation.id}`}
                  >
                    Remove from Saved
                  </button>
                )}
                {isOwn && (
                  <button
                    className="owner-menu-item owner-menu-item-danger"
                    role="menuitem"
                    onClick={() => {
                      setShowMenu(false);
                      setShowDeleteConfirm(true);
                    }}
                    data-testid={`delete-rec-${recommendation.id}`}
                  >
                    Delete recommendation
                  </button>
                )}
                {!isOwn && isAuthenticated && (
                  <button
                    className="owner-menu-item owner-menu-item-danger"
                    role="menuitem"
                    onClick={() => {
                      setShowMenu(false);
                      setReportReason('');
                      setShowReport(true);
                    }}
                    data-testid={`report-rec-${recommendation.id}`}
                  >
                    Report recommendation
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        </div>
      </div>

      <div
        className="rc-category"
        style={categoryAccentStyle(recommendation.category)}
        data-testid="rec-category"
      >
        <CategoryIcon className="rc-category-icon" />
        <span>{recommendation.category}</span>
      </div>

        <span className="rc-title-row">
          <h3 className="rc-title" data-testid="rec-name">{recommendation.name}</h3>
          <VisibilityBadge visibility={recommendation.visibility} showConnections={isOwn} />
        </span>

      <p className="rc-why-label">
        {isOwn ? 'Why you recommend it' : `Why ${firstName} recommends it`}
      </p>
      <p className="rc-reason" data-testid="rec-reason">{recommendation.reason}</p>

      {showReport && (
        <div
          className="limit-modal-overlay"
          onClick={() => !reporting && setShowReport(false)}
          data-testid={`report-dialog-${recommendation.id}`}
        >
          <div className="limit-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="limit-modal-title">Report this recommendation?</h3>
            <p className="limit-modal-text">
              Tell us what the problem is. Your name is never shown to whoever reviews it, and
              you won&apos;t see this recommendation in Explore again.
            </p>
            <div className="report-reasons" role="radiogroup" aria-label="Reason for reporting">
              {REPORT_REASONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={reportReason === value}
                  className={`report-reason ${reportReason === value ? 'active' : ''}`}
                  onClick={() => setReportReason(value)}
                  data-testid={`report-reason-${value}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="limit-modal-actions">
              <button
                className="limit-modal-btn secondary"
                onClick={() => setShowReport(false)}
                disabled={reporting}
                data-testid="report-cancel"
              >
                Cancel
              </button>
              <button
                className="limit-modal-btn primary"
                onClick={handleReport}
                disabled={reporting || !reportReason}
                data-testid="report-submit"
              >
                {reporting ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div
          className="limit-modal-overlay"
          onClick={() => !deleting && setShowDeleteConfirm(false)}
          data-testid={`delete-confirm-${recommendation.id}`}
        >
          <div className="limit-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="limit-modal-title">Delete this recommendation?</h3>
            <p className="limit-modal-text">
              This will permanently remove your recommendation and it will also disappear from
              anyone&apos;s Saved recommendations.
            </p>
            <div className="limit-modal-actions">
              <button
                className="limit-modal-btn secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                data-testid="delete-cancel"
              >
                Cancel
              </button>
              <button
                className="limit-modal-btn primary"
                onClick={handleDelete}
                disabled={deleting}
                autoFocus
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};
